const div = (a, b) => Math.trunc(a / b);
const mod = (a, b) => a - Math.trunc(a / b) * b;
const pad = (value) => String(value).padStart(2, "0");

export const padPart = (value) => String(value).padStart(2, "0");

export const normalizeCalendarDigits = (value) => String(value || "").replace(/[۰-۹٠-٩]/g, (digit) => {
    const persian = "۰۱۲۳۴۵۶۷۸۹".indexOf(digit);
    return String(persian >= 0 ? persian : "٠١٢٣٤٥٦٧٨٩".indexOf(digit));
});

const jalCal = (jy) => {
    const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
    const gy = jy + 621;
    let leapJ = -14;
    let jp = breaks[0];
    let jm = 0;
    let jump = 0;

    for (let index = 1; index < breaks.length; index += 1) {
        jm = breaks[index];
        jump = jm - jp;
        if (jy < jm) {
            break;
        }
        leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
        jp = jm;
    }

    let n = jy - jp;
    leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
    if (mod(jump, 33) === 4 && jump - n === 4) {
        leapJ += 1;
    }

    const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
    const march = 20 + leapJ - leapG;
    if (jump - n < 6) {
        n = n - jump + div(jump + 4, 33) * 33;
    }

    let leap = mod(mod(n + 1, 33) - 1, 4);
    if (leap === -1) {
        leap = 4;
    }
    return { leap, gy, march };
};

const gregorianToDay = (gy, gm, gd) => (
    div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
    + div(153 * mod(gm + 9, 12) + 2, 5)
    + gd
    - 34840408
    - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4)
    + 752
);

const dayToGregorian = (jdn) => {
    let j = 4 * jdn + 139361631;
    j += div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
    const i = div(mod(j, 1461), 4) * 5 + 308;
    const gd = div(mod(i, 153), 5) + 1;
    const gm = mod(div(i, 153), 12) + 1;
    const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
    return { gy, gm, gd };
};

const jalaliToDay = (jy, jm, jd) => {
    const result = jalCal(jy);
    return gregorianToDay(result.gy, 3, result.march)
        + (jm - 1) * 31
        - div(jm, 7) * (jm - 7)
        + jd
        - 1;
};

const dayToJalali = (jdn) => {
    const gy = dayToGregorian(jdn).gy;
    let jy = gy - 621;
    const result = jalCal(jy);
    const firstFarvardin = gregorianToDay(gy, 3, result.march);
    let offset = jdn - firstFarvardin;
    if (offset >= 0) {
        if (offset <= 185) {
            return { jy, jm: 1 + div(offset, 31), jd: mod(offset, 31) + 1 };
        }
        offset -= 186;
    } else {
        jy -= 1;
        offset += 179 + (result.leap === 1 ? 1 : 0);
    }
    return { jy, jm: 7 + div(offset, 30), jd: mod(offset, 30) + 1 };
};

export const toJalali = (date) => dayToJalali(
    gregorianToDay(date.getFullYear(), date.getMonth() + 1, date.getDate())
);

export const toGregorianDate = (year, month, day, hour = 0, minute = 0) => {
    const result = dayToGregorian(jalaliToDay(year, month, day));
    return new Date(result.gy, result.gm - 1, result.gd, hour, minute, 0, 0);
};

export const isPersianLeapYear = (year) => jalCal(year).leap === 0;

export const daysInPersianMonth = (year, month) => {
    if (month <= 6) {
        return 31;
    }
    if (month <= 11) {
        return 30;
    }
    return isPersianLeapYear(year) ? 30 : 29;
};

export const parseValue = (value, calendarSystem) => {
    const normalized = normalizeCalendarDigits(value).trim();
    const match = normalized.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2}))?/);
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    return calendarSystem === "Persian"
        ? toGregorianDate(year, month, day, hour, minute)
        : new Date(year, month - 1, day, hour, minute, 0, 0);
};

export const formatValue = (state) => {
    const { calendarSystem, year, month, day, hour, minute, mode } = state;
    const separator = calendarSystem === "Persian" ? "/" : "-";
    const date = [year, pad(month), pad(day)].join(separator);
    return mode === "datetime" ? `${date} ${pad(hour)}:${pad(minute)}` : date;
};

export const stateFromDate = (date, calendarSystem, mode) => {
    const parts = calendarSystem === "Persian"
        ? toJalali(date)
        : { jy: date.getFullYear(), jm: date.getMonth() + 1, jd: date.getDate() };
    return {
        calendarSystem,
        mode,
        year: parts.jy,
        month: parts.jm,
        day: parts.jd,
        hour: date.getHours(),
        minute: date.getMinutes()
    };
};

export const daysInMonth = (year, month, calendarSystem) => (
    calendarSystem === "Persian"
        ? daysInPersianMonth(year, month)
        : new Date(year, month, 0).getDate()
);

export const weekday = (year, month, day, calendarSystem) => (
    calendarSystem === "Persian"
        ? toGregorianDate(year, month, day).getDay()
        : new Date(year, month - 1, day).getDay()
);

export const startOfDay = (date) => {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
};

export const addDays = (date, days) => {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
};

export const startOfWeek = (date, firstDay = 1) => {
    const value = startOfDay(date);
    const offset = (value.getDay() - firstDay + 7) % 7;
    value.setDate(value.getDate() - offset);
    return value;
};

export const sameDay = (left, right) => (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
);

export const toLocalInputValue = (date) => (
    new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
);
